import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import EventDetailModal from '../components/EventDetailModal';
import { FrontendEvent } from '@/services/transform';
import { getPublicEventById } from '@/services/events.api';
import { useTranslation } from 'react-i18next';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<FrontendEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) {
        setError(isZh ? '活动ID不存在' : 'Event ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const eventData = await getPublicEventById(id);
        if (eventData) {
          setEvent(eventData);
        } else {
          setError(isZh ? '活动不存在' : 'Event not found');
        }
      } catch (err) {
        console.error('获取活动详情失败:', err);
        setError(isZh ? '获取活动详情失败' : 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, isZh]);

  // 页面加载时自动打开Modal
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C7DA0] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#0A2540]/60">{isZh ? '加载中...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h1 className="text-2xl font-bold text-[#0A2540] mb-2">
              {error || (isZh ? '活动不存在' : 'Event not found')}
            </h1>
            <p className="text-[#0A2540]/60 mb-6">
              {isZh ? '该活动可能已被删除或链接无效' : 'This event may have been removed or the link is invalid'}
            </p>
            <a
              href="/events"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C7DA0] text-white rounded-lg hover:bg-[#1a5a7a] transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {isZh ? '返回活动列表' : 'Back to Events'}
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        {/* 占位内容，会被Modal覆盖 */}
        <div className="h-[60vh] flex items-center justify-center bg-gradient-to-br from-[#0A2540] to-[#2C7DA0]">
          <div className="text-center text-white">
            <div className="animate-pulse mb-4">
              <i className="ri-calendar-event-line text-6xl" />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              {isZh ? event.title : event.titleEn}
            </h1>
            <p className="text-white/80">
              {isZh ? '正在加载活动详情...' : 'Loading event details...'}
            </p>
          </div>
        </div>
      </main>
      <Footer />
      
      {/* 自动打开活动详情Modal */}
      <EventDetailModal
        event={event}
        isZh={isZh}
        onClose={() => {
          // Modal关闭时返回活动列表
          window.location.href = '/events';
        }}
      />
    </div>
  );
}
